import type { EmploymentType, Gender, LeaveType } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { LEAVE_TYPE_LABEL } from "@/lib/labels";
import { cn } from "@/lib/cn";

/**
 * Display mirror of hrms-backend/src/utils/leaveEntitlements.js.
 *
 * The backend remains the only thing that DECIDES eligibility: every balance
 * and every list of applicable types on screen comes from /api/leave/balance,
 * /api/leave/types or /api/*\/leave-summary. This table exists so a reader can
 * see WHY they got the answer they got, which was otherwise invisible.
 *
 * If LEAVE_TYPE_RULES changes on the backend, this file changes in the same
 * commit.
 */

export interface PolicyRule {
  type: LeaveType;
  annual: string;
  accrual: string;
  carryForward: string;
  employmentTypes: EmploymentType[];
  /** Only Menstrual Leave has one today. */
  genderRule?: { excludedWhen: Gender; note: string };
}

export const LEAVE_POLICY: PolicyRule[] = [
  {
    type: "EL",
    annual: "12 days",
    accrual: "1 day a month",
    carryForward: "Carries forward, capped at 40 days",
    employmentTypes: ["FTE"],
  },
  {
    type: "CL",
    annual: "6 days",
    accrual: "0.5 days a month",
    carryForward: "Lapses at year end",
    employmentTypes: ["FTE", "Intern"],
  },
  {
    type: "SL",
    annual: "6 days",
    accrual: "0.5 days a month",
    carryForward: "Lapses at year end",
    employmentTypes: ["FTE", "Intern"],
  },
  {
    type: "Menstrual",
    annual: "12 days",
    accrual: "1 day a month",
    carryForward: "Lapses at year end",
    employmentTypes: ["FTE", "Intern"],
    genderRule: {
      excludedWhen: "Male",
      note: "Hidden once an account is explicitly marked Male. While gender is unset the benefit stays visible, so it is never silently withheld from someone whose record is simply incomplete.",
    },
  },
  {
    type: "Special",
    annual: "Not yet defined",
    accrual: "Flat, not time-accrued",
    carryForward: "Never resets",
    employmentTypes: ["FTE"],
  },
  {
    type: "OptionalHoliday",
    annual: "2 days",
    accrual: "Flat, per calendar year",
    carryForward: "Resets each year",
    employmentTypes: ["FTE"],
  },
  {
    type: "CompOff",
    annual: "Earned, not granted",
    accrual: "Credited for working a weekly off or company holiday",
    carryForward: "Held until taken",
    employmentTypes: ["FTE", "Intern"],
  },
  {
    // LWP is in LEAVE_TYPE_RULES and comes back from /api/leave/types, so it is
    // offered in the apply dropdown and gets its own balance meter. Leaving it
    // out of this table meant the reference headed "Why you get what you get"
    // denied the existence of a type people could already apply for.
    type: "LWP",
    annual: "No limit",
    accrual: "Not accrued — applied for as needed",
    carryForward: "Nothing to carry: the day is simply unpaid",
    employmentTypes: ["FTE", "Intern"],
  },
];

/** Mirrors eligibleLeaveTypesFor(): a gender rule only ever excludes on an
 *  explicit opposite value, so an unset gender always fails open. */
export function isEligible(
  rule: PolicyRule,
  employmentType: EmploymentType | null | undefined,
  gender: Gender | null | undefined,
): boolean {
  if (employmentType && !rule.employmentTypes.includes(employmentType)) return false;
  if (rule.genderRule && gender === rule.genderRule.excludedWhen) return false;
  return true;
}

/**
 * The policy reference. Pass `employmentType`/`gender` to mark which rows apply
 * to one person and grey out the rest.
 */
export function LeavePolicyPanel({
  title = "Leave policy",
  note,
  employmentType,
  gender,
  scoped,
}: {
  title?: string;
  note?: string;
  employmentType?: EmploymentType | null;
  gender?: Gender | null;
  /** When true, renders the "applies to you" column. */
  scoped?: boolean;
}) {
  // Until the account's employment type is known there is no answer to "does
  // this apply to you", and a Yes/No badge would be an assertion rather than a
  // fact. The column stays, so the table does not reflow, and says so.
  const scopeKnown = Boolean(employmentType);

  return (
    <Panel>
      <PanelHeader
        title={title}
        note={
          note ??
          "Enforced by the backend. Balances on screen are always what the server calculated, never a client-side guess."
        }
      />
      <TableWrap>
        <THead>
          <TH>Leave type</TH>
          <TH>Entitlement</TH>
          <TH>How it accrues</TH>
          <TH>Carry forward</TH>
          <TH>Who gets it</TH>
          {scoped && <TH>Applies to you</TH>}
        </THead>
        <TBody>
          {LEAVE_POLICY.map((rule) => {
            const applies = isEligible(rule, employmentType, gender);
            return (
              <TR key={rule.type} className={scoped && scopeKnown && !applies ? "opacity-55" : undefined}>
                <TDPrimary>
                  <span className="flex items-center gap-2">
                    {LEAVE_TYPE_LABEL[rule.type]}
                    {rule.genderRule && (
                      <Icon
                        name="info"
                        size={16}
                        className="text-faint"
                        aria-label="Has a gender-based eligibility rule"
                      />
                    )}
                  </span>
                </TDPrimary>
                <TD>{rule.annual}</TD>
                <TD>{rule.accrual}</TD>
                <TD>{rule.carryForward}</TD>
                <TD>
                  <span className="flex flex-wrap gap-1">
                    {rule.employmentTypes.map((t) => (
                      <Badge key={t} tone="outline">
                        {t === "FTE" ? "Full-time" : "Intern"}
                      </Badge>
                    ))}
                    {rule.genderRule && (
                      <Badge tone="neutral">Not if {rule.genderRule.excludedWhen}</Badge>
                    )}
                  </span>
                </TD>
                {scoped && (
                  <TD>
                    {!scopeKnown ? (
                      <span className="text-muted">—</span>
                    ) : applies ? (
                      <Badge tone="good" icon="check-circle">
                        Yes
                      </Badge>
                    ) : (
                      <Badge tone="neutral" icon="blocked">
                        No
                      </Badge>
                    )}
                  </TD>
                )}
              </TR>
            );
          })}
        </TBody>
      </TableWrap>

      <div className="border-t border-line px-4 py-3">
        <p className="flex items-start gap-2 text-xs leading-relaxed text-muted">
          <Icon name="info" size={16} className="mt-px shrink-0 text-faint" />
          <span>
            {LEAVE_POLICY.find((r) => r.genderRule)?.genderRule?.note}
          </span>
        </p>
      </div>
    </Panel>
  );
}

/**
 * The live consequence of a gender or employment-type choice, shown inside the
 * account form so HR can see what a change does before saving it.
 */
export function EligibilityPreview({
  employmentType,
  gender,
  className,
}: {
  employmentType: EmploymentType;
  gender: Gender | "";
  className?: string;
}) {
  const eligible = LEAVE_POLICY.filter((rule) => isEligible(rule, employmentType, gender || null));
  const excluded = LEAVE_POLICY.filter((rule) => !isEligible(rule, employmentType, gender || null));

  return (
    <div className={cn("rounded-lg border border-line bg-surface-sunken p-3", className)}>
      <p className="eyebrow mb-2">Leave this account will be eligible for</p>
      <div className="flex flex-wrap gap-1.5">
        {eligible.map((rule) => (
          <Badge key={rule.type} tone="good" icon="check">
            {LEAVE_TYPE_LABEL[rule.type]}
          </Badge>
        ))}
      </div>
      {excluded.length > 0 && (
        <>
          <p className="mt-3 text-xs text-muted">Not eligible</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {excluded.map((rule) => (
              <Badge key={rule.type} tone="neutral">
                {LEAVE_TYPE_LABEL[rule.type]}
              </Badge>
            ))}
          </div>
        </>
      )}
      {!gender && (
        <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-warn">
          <Icon name="warning-circle" size={16} className="mt-px shrink-0" />
          <span>
            Gender is not set, so Menstrual Leave stays visible. Set it to apply the rule properly.
          </span>
        </p>
      )}
    </div>
  );
}
